import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    intl,
    fetchData,
    getToken,
    DmsFlexContainer,
    DmsModal
} from "dmsbasereact";
import {
    Box,
    Card,
    CardContent,
    CardActionArea,
    Grid,
    Typography,
    Chip,
    CircularProgress,
    Alert,
    IconButton,
    Slider,
    LinearProgress,
    Button,
    Tooltip
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SchoolIcon from "@mui/icons-material/School";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import { getBaseURL } from "../../../build_config";

const getFileExtension = (filename) => {
    if (!filename) return "";
    return filename.split(".").pop().toLowerCase();
};

const FILE_TYPE_CONFIG = {
    video: { extensions: ["mp4", "webm", "mov", "avi"], icon: OndemandVideoIcon, color: "#e53935" },
    pdf: { extensions: ["pdf"], icon: PictureAsPdfIcon, color: "#d32f2f" },
    image: { extensions: ["jpg", "jpeg", "png", "gif", "webp"], icon: ImageIcon, color: "#43a047" }
};

const getFileTypeInfo = (filename) => {
    const ext = getFileExtension(filename);

    for (const [type, config] of Object.entries(FILE_TYPE_CONFIG)) {
        if (config.extensions.includes(ext)) {
            return { type, icon: config.icon, color: config.color };
        }
    }
    return { type: "file", icon: InsertDriveFileIcon, color: "#757575" };
};

const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds) || timeInSeconds === 0) return "00:00";
    const date = new Date(0);
    date.setSeconds(timeInSeconds);
    return date.toISOString().substr(timeInSeconds >= 3600 ? 11 : 14, timeInSeconds >= 3600 ? 8 : 5);
};

const TrainingCard = ({ material, onClick }) => {
    const filename = material.file?.original_filename || "";
    const fileInfo = getFileTypeInfo(filename);
    const FileIcon = fileInfo.icon;
    const trainingName = material.training?.name || intl.get("UnknownTraining").d("Unknown Training");
    const trainingDescription = material.training?.description || "";

    const getFileTypeLabel = () => {
        switch (fileInfo.type) {
            case "video": return intl.get("Video").d("Video");
            case "pdf": return "PDF";
            case "image": return intl.get("Image").d("Image");
            default: return intl.get("File").d("File");
        }
    };

    const getFileTypeColor = () => {
        if (fileInfo.type === "video") return "error";
        if (fileInfo.type === "pdf") return "warning";
        return "default";
    };

    return (
        <Card
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6
                }
            }}
        >
            <CardActionArea onClick={() => onClick(material)}>
                <Box
                    sx={{
                        height: 160,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "grey.100",
                        position: "relative"
                    }}
                >
                    <FileIcon sx={{ fontSize: 70, color: fileInfo.color, opacity: 0.8 }} />
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            bgcolor: "rgba(0,0,0,0.5)",
                            borderRadius: "50%",
                            p: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: 0,
                            transition: "opacity 0.2s",
                            ".MuiCardActionArea-root:hover &": {
                                opacity: 1
                            }
                        }}
                    >
                        {fileInfo.type === "video" ? (
                            <PlayCircleOutlineIcon sx={{ fontSize: 40, color: "white" }} />
                        ) : (
                            <VisibilityIcon sx={{ fontSize: 40, color: "white" }} />
                        )}
                    </Box>
                </Box>
            </CardActionArea>
            <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                <Typography variant="h6" component="div" noWrap title={trainingName}>
                    {trainingName}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        minHeight: 40
                    }}
                >
                    {trainingDescription || intl.get("NoDescription").d("No description")}
                </Typography>
                <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    <Chip
                        size="small"
                        label={getFileTypeLabel()}
                        color={getFileTypeColor()}
                        variant="outlined"
                    />
                    {filename && (
                        <Chip
                            size="small"
                            label={filename.length > 20 ? filename.substring(0, 17) + "..." : filename}
                            variant="outlined"
                            sx={{ color: "text.secondary" }}
                        />
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

const PreviewHeader = ({ title, description, gradient, children }) => (
    <Box sx={{
        py: 2,
        px: 3,
        textAlign: "center",
        background: gradient,
        color: "white"
    }}>
        <Typography
            variant="h5"
            sx={{
                fontWeight: 600,
                mb: 0.5,
                textShadow: "0 1px 5px rgba(0,0,0,0.3)"
            }}
        >
            {title}
        </Typography>
        {description && (
            <Typography
                variant="body2"
                sx={{
                    color: "rgba(255,255,255,0.9)",
                    mb: 1,
                    maxWidth: "500px",
                    mx: "auto"
                }}
            >
                {description}
            </Typography>
        )}
        {children}
    </Box>
);

const VideoPreview = ({ contentUrl, trainingName, trainingDescription, setError }) => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(1);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [videoProgress, setVideoProgress] = useState(0);

    const togglePlayPause = () => {
        if (videoRef.current) {
            if (videoRef.current.paused) {
                videoRef.current.play();
            } else {
                videoRef.current.pause();
            }
        }
    };

    const handleVolumeChange = (newVolume) => {
        if (videoRef.current) {
            videoRef.current.volume = newVolume;
            setVolume(newVolume);
        }
    };

    const handleTimeUpdate = (e) => {
        const current = e.target.currentTime;
        const dur = e.target.duration;
        setCurrentTime(current);
        if (dur > 0) {
            setVideoProgress((current / dur) * 100);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <PreviewHeader
                title={trainingName}
                description={trainingDescription}
                gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            >
                <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                    <Chip
                        icon={<AccessTimeIcon sx={{ color: "white !important" }} />}
                        label={`${intl.get("Duration").d("Duration")}: ${formatTime(duration)}`}
                        variant="outlined"
                        size="small"
                        sx={{
                            borderColor: "rgba(255,255,255,0.5)",
                            color: "white",
                            backgroundColor: "rgba(255,255,255,0.1)"
                        }}
                    />
                    <Chip
                        label={intl.get("PreviewMode").d("Preview Mode")}
                        size="small"
                        sx={{
                            borderColor: "rgba(255,255,255,0.5)",
                            color: "white",
                            backgroundColor: "rgba(255,255,255,0.2)"
                        }}
                    />
                </Box>
            </PreviewHeader>

            <Box sx={{
                backgroundColor: "#000",
                display: "flex",
                flexDirection: "column"
            }}>
                <Box sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 2
                }}>
                    <video
                        ref={videoRef}
                        src={contentUrl}
                        playsInline
                        style={{
                            maxWidth: "100%",
                            maxHeight: "40vh",
                            width: "auto",
                            height: "auto"
                        }}
                        onLoadedMetadata={(e) => setDuration(e.target.duration)}
                        onTimeUpdate={handleTimeUpdate}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onError={() => setError(intl.get("VideoPlaybackError").d("Error playing video. The file may be corrupted or in an unsupported format."))}
                    >
                        {intl.get("BrowserDoesNotSupportVideo").d("Your browser does not support the video tag.")}
                    </video>
                </Box>

                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.5,
                    background: "rgba(0, 0, 0, 0.6)"
                }}>
                    <Tooltip title={isPlaying ? intl.get("Tooltip.Action.Pause").d("Pause") : intl.get("Tooltip.Action.Play").d("Play")}>
                        <IconButton onClick={togglePlayPause} sx={{ color: "white" }}>
                            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={volume === 0 ? intl.get("Tooltip.Action.Unmute").d("Unmute") : intl.get("Tooltip.Action.Mute").d("Mute")}>
                        <IconButton
                            onClick={() => handleVolumeChange(volume > 0 ? 0 : 1)}
                            sx={{ color: "white" }}
                        >
                            {volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
                        </IconButton>
                    </Tooltip>
                    <Slider
                        value={volume * 100}
                        onChange={(e, newValue) => handleVolumeChange(newValue / 100)}
                        aria-label={intl.get("Volume").d("Volume")}
                        min={0}
                        max={100}
                        sx={{ width: 100, color: "white" }}
                    />
                </Box>
            </Box>

            <LinearProgress
                variant="determinate"
                value={videoProgress}
                sx={{
                    height: 6,
                    backgroundColor: "rgba(102,126,234,0.2)",
                    "& .MuiLinearProgress-bar": {
                        background: "linear-gradient(90deg, #00ff88 0%, #00d4ff 100%)"
                    }
                }}
            />

            <Box sx={{
                p: 2,
                backgroundColor: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderTop: "1px solid #e0e0e0"
            }}>
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.3 }}>
                        {intl.get("Progress").d("Video Progress")}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            fontWeight: 600,
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent"
                        }}
                    >
                        {formatTime(currentTime)} / {formatTime(duration)}
                    </Typography>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                        {Math.round(videoProgress)}% {intl.get("Completed").d("Completed")}
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    color={videoProgress >= 100 ? "success" : "primary"}
                    startIcon={videoProgress >= 100 ? <CheckCircleOutlineIcon /> : null}
                    disabled={videoProgress < 100}
                    sx={{
                        borderRadius: "20px",
                        textTransform: "none",
                        fontWeight: "600",
                        px: 3,
                        py: 1,
                        background: videoProgress >= 100
                            ? "linear-gradient(135deg, #4caf50 0%, #45a049 100%)"
                            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        boxShadow: "0 6px 20px rgba(102,126,234,0.3)",
                        "&:disabled": {
                            background: "rgba(0, 0, 0, 0.12)"
                        }
                    }}
                >
                    {intl.get("MarkAsComplete").d("Mark as Complete")}
                </Button>
            </Box>
        </Box>
    );
};

const PdfPreview = ({ contentUrl, trainingName, trainingDescription, filename }) => (
    <Box sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f5f5f7",
        borderRadius: 2,
        overflow: "hidden"
    }}>
        <PreviewHeader
            title={trainingName}
            description={trainingDescription}
            gradient="linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)"
        />
        <Box sx={{ width: "100%", height: 500 }}>
            <iframe
                src={contentUrl}
                style={{
                    width: "100%",
                    height: "100%",
                    border: "none"
                }}
                title={filename}
            />
        </Box>
    </Box>
);

const ImagePreview = ({ contentUrl, trainingName, trainingDescription, filename }) => (
    <Box sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f5f5f7",
        borderRadius: 2,
        overflow: "hidden"
    }}>
        <PreviewHeader
            title={trainingName}
            description={trainingDescription}
            gradient="linear-gradient(135deg, #43a047 0%, #2e7d32 100%)"
        />
        <Box sx={{ textAlign: "center", py: 3, px: 2, backgroundColor: "#000" }}>
            <img
                src={contentUrl}
                alt={filename}
                style={{ maxWidth: "100%", maxHeight: 500, objectFit: "contain" }}
            />
        </Box>
    </Box>
);

const MaterialPreviewModal = ({ open, onClose, material }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [contentUrl, setContentUrl] = useState(null);

    useEffect(() => {
        let objectUrl = null;

        const loadContent = async () => {
            if (!material?.file_pk) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);
            setContentUrl(null);

            try {
                const fileUrl = `${getBaseURL()}/training_material/file/${material.file_pk}`;
                const token = getToken();

                const response = await fetch(fileUrl, {
                    headers: { Authorization: `Bearer ${token.access_token}` }
                });

                if (!response.ok) {
                    throw new Error(`Failed to load file: ${response.status} ${response.statusText}`);
                }

                const contentType = response.headers.get("content-type") || "application/octet-stream";
                const blob = await response.blob();
                const typedBlob = blob.type ? blob : new Blob([blob], { type: contentType });

                objectUrl = URL.createObjectURL(typedBlob);
                setContentUrl(objectUrl);
            } catch (err) {
                setError(err.message || intl.get("ErrorLoadingContent").d("Error loading content"));
            } finally {
                setLoading(false);
            }
        };

        if (open && material) {
            loadContent();
        }

        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [open, material]);

    useEffect(() => {
        if (!open) {
            setContentUrl(null);
            setLoading(true);
            setError(null);
        }
    }, [open]);

    if (!material) return null;

    const filename = material.file?.original_filename || "";
    const fileInfo = getFileTypeInfo(filename);
    const trainingName = material.training?.name || intl.get("UnknownTraining").d("Unknown Training");
    const trainingDescription = material.training?.description || "";

    const renderContent = () => {
        if (loading) {
            return (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 400,
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        borderRadius: 2,
                        color: "white"
                    }}
                >
                    <CircularProgress size={60} thickness={4} sx={{ mb: 3, color: "white" }} />
                    <Typography variant="h6" sx={{ fontWeight: 500, textShadow: "0 2px 10px rgba(0,0,0,0.3)" }}>
                        {intl.get("LoadingContent").d("Loading training experience...")}
                    </Typography>
                </Box>
            );
        }

        if (error) {
            return (
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            );
        }

        if (!contentUrl) {
            return (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <InsertDriveFileIcon sx={{ fontSize: 60, color: "grey.400" }} />
                    <Typography color="text.secondary">
                        {intl.get("FileNotAvailable").d("File not available")}
                    </Typography>
                </Box>
            );
        }

        switch (fileInfo.type) {
            case "video":
                return (
                    <VideoPreview
                        contentUrl={contentUrl}
                        trainingName={trainingName}
                        trainingDescription={trainingDescription}
                        setError={setError}
                    />
                );
            case "pdf":
                return (
                    <PdfPreview
                        contentUrl={contentUrl}
                        trainingName={trainingName}
                        trainingDescription={trainingDescription}
                        filename={filename}
                    />
                );
            case "image":
                return (
                    <ImagePreview
                        contentUrl={contentUrl}
                        trainingName={trainingName}
                        trainingDescription={trainingDescription}
                        filename={filename}
                    />
                );
            default:
                return (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        <InsertDriveFileIcon sx={{ fontSize: 60, color: "grey.400" }} />
                        <Typography color="text.secondary" sx={{ mt: 2 }}>
                            {intl.get("PreviewNotAvailable").d("Preview not available for this file type")}
                        </Typography>
                    </Box>
                );
        }
    };

    return (
        <DmsModal
            isVisible={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    maxHeight: "90vh",
                    height: "auto"
                }
            }}
        >
            <DmsModal.Header>
                {intl.get("TrainingPreview").d("Training Preview")}
            </DmsModal.Header>
            <DmsModal.Body sx={{ padding: 0, overflow: "auto" }}>
                {renderContent()}
            </DmsModal.Body>
        </DmsModal>
    );
};

export const VideosTab = () => {
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedMaterial, setSelectedMaterial] = useState(null);

    const fetchMaterials = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchData("/training_material/all", "GET", true);
            const data = response.data?.data || response.data || [];
            setMaterials(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message || intl.get("ErrorLoadingMaterials").d("Error loading training materials"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMaterials();
    }, [fetchMaterials]);

    const handleCardClick = (material) => {
        setSelectedMaterial(material);
        setPreviewOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
        setSelectedMaterial(null);
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box>
            <DmsFlexContainer sx={{ mb: 3 }}>
                <DmsFlexContainer.Left>
                    <Typography variant="h6" color="text.secondary">
                        {intl.get("TrainingPreviewDescription").d("Preview of configured training materials")}
                    </Typography>
                </DmsFlexContainer.Left>
            </DmsFlexContainer>

            {materials.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <SchoolIcon sx={{ fontSize: 60, color: "grey.400", mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        {intl.get("NoTrainingMaterialsFound").d("No training materials found")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {intl.get("ConfigureTrainingMaterials").d("Configure training materials in the other tabs")}
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {materials.map((material) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={material.pk}>
                            <TrainingCard material={material} onClick={handleCardClick} />
                        </Grid>
                    ))}
                </Grid>
            )}

            <MaterialPreviewModal
                open={previewOpen}
                onClose={handleClosePreview}
                material={selectedMaterial}
            />
        </Box>
    );
};
